import { useState } from 'react';

import { addGuestLike, removeGuestLike } from '../helpers';
import { type GuestLikeProductInput } from '../types';

import { useShowGuestLikesToast } from './useGuestLikesToast';
import { useIsGuestLiked } from './useIsGuestLiked';

import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking';
import { PRODUCT_INTERACTION_TYPES } from '@/modules/product/constants';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';

export function useGuestLikes(productId: number) {
  const isLiked = useIsGuestLiked(productId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sendGuestLikesToast = useShowGuestLikesToast();
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const {
    gtm: { sendEvent },
  } = useExternalTracking();

  function trackLike(interactionType: string, action: string) {
    sendProductInteractionActionATEvent(productId, interactionType);
    sendEvent({
      category: 'Product Interaction',
      action,
      label: productId.toString(),
    });
  }

  function toggleGuestLike(product: GuestLikeProductInput) {
    if (isLiked) {
      removeGuestLike(productId);
      trackLike(PRODUCT_INTERACTION_TYPES.PRODUCT_UNLIKE, 'unlike product');
      return 'removed';
    }
    const result = addGuestLike({ ...product, timestamp: Date.now() });
    if (result === 'lastLike') {
      sendGuestLikesToast();
    }
    if (result === 'reachedCap') {
      setIsModalOpen(true);
      return result;
    }
    trackLike(PRODUCT_INTERACTION_TYPES.PRODUCT_LIKE, 'like product');
    return result;
  }

  return { isLiked, toggleGuestLike, isModalOpen, setIsModalOpen };
}
